import type { Tone } from './workstations';

/** Miroir des énumérations PostgreSQL de l'inventaire de pièces. */
export const PART_CATEGORIES = [
  'memoire',
  'stockage',
  'alimentation',
  'carteGraphique',
  'processeur',
  'carteMere',
  'batterie',
  'ecran',
  'clavier',
  'ventilateur',
  'autre',
] as const;
export type PartCategory = (typeof PART_CATEGORIES)[number];

export const PART_TRACKINGS = ['exemplaire', 'quantite'] as const;
export type PartTracking = (typeof PART_TRACKINGS)[number];

export const PART_ITEM_STATES = ['neuf', 'enService', 'retire', 'defectueux'] as const;
export type PartItemState = (typeof PART_ITEM_STATES)[number];

/** Opérations qu'un changement de pièce peut décrire. */
export const PART_OPERATIONS = ['montage', 'retrait', 'remplacement'] as const;
export type PartOperation = (typeof PART_OPERATIONS)[number];

export const PART_CATEGORY_LABELS: Record<PartCategory, string> = {
  memoire: 'Mémoire',
  stockage: 'Stockage',
  alimentation: 'Alimentation',
  carteGraphique: 'Carte graphique',
  processeur: 'Processeur',
  carteMere: 'Carte mère',
  batterie: 'Batterie',
  ecran: 'Écran',
  clavier: 'Clavier',
  ventilateur: 'Ventilateur',
  autre: 'Autre',
};

export const PART_TRACKING_LABELS: Record<PartTracking, string> = {
  exemplaire: "À l'exemplaire",
  quantite: 'À la quantité',
};

export const PART_TRACKING_HINTS: Record<PartTracking, string> = {
  exemplaire:
    'Chaque pièce physique porte son identité et son historique de localisation.',
  quantite: 'Seule la quantité en stock existe, sans pièce individuelle.',
};

export const PART_ITEM_STATE_LABELS: Record<PartItemState, string> = {
  neuf: 'Neuf',
  enService: 'En service',
  retire: 'Retiré',
  defectueux: 'Défectueux',
};

export const PART_ITEM_STATE_TONES: Record<PartItemState, Tone> = {
  neuf: 'accent',
  enService: 'ok',
  retire: 'neutre',
  defectueux: 'danger',
};

export const PART_OPERATION_LABELS: Record<PartOperation, string> = {
  montage: 'Montage',
  retrait: 'Retrait',
  remplacement: 'Remplacement',
};

export function partCategoryLabel(category: string): string {
  return PART_CATEGORY_LABELS[category as PartCategory] ?? category;
}

export function partItemStateLabel(state: string): string {
  return PART_ITEM_STATE_LABELS[state as PartItemState] ?? state;
}

export function partOperationLabel(operation: string): string {
  return PART_OPERATION_LABELS[operation as PartOperation] ?? operation;
}

/**
 * États qui rendent un exemplaire indisponible. Sert à ne pas proposer au
 * montage ce que le serveur refuserait ; la règle qui fait autorité est la
 * fonction SQL part_state_is_available.
 */
const UNAVAILABLE_STATES: readonly PartItemState[] = ['retire', 'defectueux'];

export function isAvailableItemState(state: string): boolean {
  return !UNAVAILABLE_STATES.includes(state as PartItemState);
}

/** Ton du stock : une valeur négative est une anomalie à régler. */
export function stockTone(stock: number): Tone {
  if (stock < 0) return 'danger';
  if (stock === 0) return 'alerte';
  return 'ok';
}
